/**
 * Cat drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_doodles`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const catDrawingsPack: ToilePack = {
    slug: 'cat-drawings',
    category: 'coloring',
    name: 'Cat drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'sleeping', label: 'Sleeping cat', subject: 'a cat asleep in a tight curl, tail over its paws and ears folded back'},
        {key: 'sitting', label: 'Sitting cat', subject: 'a cat sitting upright seen from behind, looking over its shoulder, tail curled around its feet'},
        {key: 'box', label: 'Cat in a box', subject: 'a kitten peeking out of a cardboard box, only its head and front paws showing over the edge'},
    ],
    styles: [
        {key: 'simple', label: 'Simple outline', style: 'a thick rounded outline, few inner lines, simple enough to copy or color', palette: 'Black ink only, no gray tones.', framing: 'One drawing centered on the page with a wide white margin.'},
        {key: 'fur', label: 'Pencil fur', style: 'a soft pencil drawing with short directional fur strokes and light shading', palette: 'Graphite gray on white paper.', framing: 'One drawing centered on the page with a wide white margin.'},
        {key: 'brush', label: 'Ink brush', style: 'a few confident brush-ink strokes with thick-to-thin lines and a dry-brush edge', palette: 'Black ink only.', framing: 'One drawing centered on the page with a wide white margin.'},
    ],
    directions: ['a black cat', 'kitten', 'with yarn'],
    directionHint: 'try: a black cat',
    seo: {
        title: 'Cat Drawings: Sleeping, Sitting, Kitten in a Box',
        metaDescription: 'Nine cat drawings, a cat asleep, a cat sitting and a kitten in a box, in simple outline, pencil fur and brush ink.',
        h1: 'Cat drawings: sleeping cat, sitting cat, cat in a box',
        tagline: 'Cats in the poses owners know, one cat to a page.',
        intro: 'Cat drawings show cats the way cat owners see them: asleep, sitting, peering out of a box. These nine pages show one cat each, drawn clearly enough to copy or color. Each comes as a simple thick outline, a soft pencil drawing with fur strokes, or a few brush-ink strokes. For a different cat, type it in the direction box.',
        useCases: [
            'A copy page for a child',
            'A coloring page',
            'A gift card drawing',
            'A sketchbook study of poses',
        ],
        faq: [
            {q: 'What is the difference between the three styles?', a: 'Simple outline has few inner lines, pencil fur adds short strokes and light shading, and ink brush uses a few strokes with thick-to-thin lines.'},
            {q: 'Are the poses fixed?', a: 'The three readings are the sleeping, sitting and boxed poses on the samples. A typed cat is a request and may differ.'},
            {q: 'Can I ask for a different cat?', a: 'Yes. Type it in the direction box, such as a black cat, a kitten or with yarn. A typed cat is a request, so the result may differ from the samples.'},
        ],
    },
}
